'use client';

import { ErrorMessage } from '@repo/ui/error-message';
import { Skeleton } from '@repo/ui/skeleton';
import { cn } from '@repo/utils/cn';
import * as Sentry from '@sentry/nextjs';
import { useLocale } from 'next-intl';
import dynamic from 'next/dynamic';
import React, { useCallback, useMemo, useState } from 'react';
import type { CryptoAddressResponseType } from '@/lib/fetch';
import { useAppStore } from '@/providers/AppStoreProvider';

const MoonPayProviderWrapper = dynamic(
  () => import('./moonpay-provider-wrapper').then((mod) => mod.MoonPayProviderWrapper),
  {
    ssr: false,
    loading: () => <Skeleton className="min-h-[600px] vt-surface-light-bg" />,
  }
);

const MoonPayBuyWidget = dynamic(() => import('@moonpay/moonpay-react').then((mod) => mod.MoonPayBuyWidget), {
  ssr: false,
  loading: () => <Skeleton className="min-h-[600px] vt-surface-light-bg" />,
});

type MoonPayProps = {
  supportedWalletAddresses: CryptoAddressResponseType[];
  locale?: string;
  theme?: 'dark' | 'light';
  email?: string;
  baseCurrencyCode?: string;
  showOnlyCurrencies?: string;
  selectedCurrency?: string;
  className?: string;
};

/**
 * Get the MoonPay signature for a given URL
 *
 * https://dev.moonpay.com/docs/on-ramp-enhance-security-using-signed-urls
 *
 * @param url - The URL to generate the signature for
 * @returns The signature for the given URL
 */
const handleGetSignature = async (url: string): Promise<string> => {
  try {
    const response = await fetch(`/api/generate-moonpay-signature?url=${encodeURIComponent(url)}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to generate signature');
    }

    const data = await response.json();

    return data.signature;
  } catch (error) {
    Sentry.captureException(error);

    // Add more context to the error
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to initialize MoonPay payment widget: ${errorMessage}`);
  }
};

/**
 * Mapping of our internal network names to MoonPay currency codes
 *
 * In order to use MoonPay, we need to map our internal network names to
 * MoonPay's currency codes so the widget can display the correct currencies
 * along with their wallet addresses.
 *
 * @see https://support.moonpay.com/customers/docs/list-of-supported-cryptocurrencies
 */
const NETWORK_MAPPING_TO_MOONPAY_CURRENCY_CODE: Record<
  string,
  { networks: string[]; internalNetworks: string[]; code: string }
> = {
  ETH: {
    networks: ['ETHEREUM'],
    internalNetworks: ['ETH'],
    code: 'eth',
  },
  BTC: {
    networks: ['BITCOIN'],
    internalNetworks: ['BTC'],
    code: 'btc',
  },
  TRX: {
    networks: ['TRON'],
    internalNetworks: ['TRON'],
    code: 'trx',
  },
  TRUMP: {
    networks: ['SOLANA'],
    internalNetworks: ['TRUMP'],
    code: 'trump_sol',
  },
  SOL: {
    networks: ['SOLANA'],
    internalNetworks: ['SOL'],
    code: 'sol',
  },
  BNB: {
    networks: ['BNB'],
    internalNetworks: ['BSC'],
    code: 'bnb_bsc',
  },
  // SHIB: {
  //   networks: ['ERC20'],
  //   internalNetworks: ['SHIBA'],
  //   code: 'shib',
  // },
  // TODO: Temporarily disabled USDT support since Moonpay uses the wrong network for USDT
  // Re-enable USDT support once Moonpay fixes the issue
  //
  // USDT: {
  //   networks: ['ERC20'],
  //   internalNetworks: ['USDT'],
  //   code: 'usdt',
  // },
  // USDT_TRX: {
  //   networks: ['TRC20'],
  //   internalNetworks: ['TRON'],
  //   code: 'usdt_trx',
  // },
  // USDT_BSC: {
  //   networks: ['BEP20'],
  //   internalNetworks: ['BSC'],
  //   code: 'usdt_bsc',
  // },
};

/**
 * Map internal representation of a network or internal network to its corresponding MoonPay currency code
 *
 * @param network - The network name to convert
 * @param internalNetwork - The internal network name to convert
 * @returns The corresponding MoonPay currency code
 */
const getNetworkMoonPayCurrencyCode = ({
  network,
  internalNetwork,
}: {
  network: string;
  internalNetwork?: string;
}): string | undefined => {
  if (!network) {
    return undefined;
  }

  const upperNetwork = network.toUpperCase();

  const entry = Object.values(NETWORK_MAPPING_TO_MOONPAY_CURRENCY_CODE).find(({ internalNetworks, networks }) => {
    if (internalNetwork) {
      const upperInternalNetwork = internalNetwork.toUpperCase();
      return internalNetworks.includes(upperInternalNetwork) && networks.includes(upperNetwork);
    }

    return networks.includes(upperNetwork);
  });

  return entry?.code;
};

/**
 * Display the MoonPay widget
 *
 * Check https://dev.moonpay.com/docs/ramps-sdk-buy-params for more information
 */
const DepositCryptoFormMoonpay = ({
  supportedWalletAddresses,
  theme = 'dark',
  baseCurrencyCode = 'usd',
  locale: propLocale,
  className,
  selectedCurrency,
}: MoonPayProps) => {
  const contextLocale = useLocale();
  const locale = propLocale || contextLocale;

  const { email } = useAppStore((state) => ({
    email: state.email,
  }));

  const [error, setError] = useState<string | null>(null);

  const walletAddresses = useMemo(
    () =>
      supportedWalletAddresses.reduce(
        (acc, { networks }) => {
          networks?.forEach(({ internalNetwork, network, walletAddress }) => {
            if (internalNetwork && network && walletAddress) {
              const currency = getNetworkMoonPayCurrencyCode({ internalNetwork, network });
              if (currency) {
                acc[currency] = walletAddress;
              }
            }
          });
          return acc;
        },
        {} as Record<string, string>
      ),
    [supportedWalletAddresses]
  );

  const onUrlSignatureRequested = useCallback(async (url: string): Promise<string> => {
    let signature = '';
    try {
      signature = await handleGetSignature(url);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      Sentry.captureException(error, {
        tags: {
          component: 'DepositCryptoFormMoonpay',
          action: 'onUrlSignatureRequested',
        },
      });
    }
    return signature;
  }, []);

  if (error) {
    return (
      <ErrorMessage
        title={error}
        className="flex min-h-[250px] items-center justify-center"
      />
    );
  }

  return (
    <MoonPayProviderWrapper>
      <MoonPayBuyWidget
        theme={theme}
        variant="embedded"
        colorCode={'#b132ff,#b132ff'}
        email={email || ''}
        walletAddresses={JSON.stringify(walletAddresses)}
        showOnlyCurrencies={Object.keys(walletAddresses).join(',')}
        language={locale}
        baseCurrencyCode={baseCurrencyCode}
        className={cn('!m-0 !w-full !rounded-[4px] !border-none !bg-transparent !p-0', className)}
        defaultCurrencyCode={
          selectedCurrency ? getNetworkMoonPayCurrencyCode({ network: selectedCurrency }) : undefined
        }
        onUrlSignatureRequested={onUrlSignatureRequested}
        visible
      />
    </MoonPayProviderWrapper>
  );
};

export default React.memo(DepositCryptoFormMoonpay);
